import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash2, CreditCard, Link as LinkIcon, Loader2, Handshake } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useBudgets } from "@/hooks/useBudgets";
import { useBusinessSettings } from "@/hooks/useBusinessSettings";
import { summarizeBarter } from "@/lib/barter";
import {
  useFinanceStats,
  useInstallments,
  useMarkInstallment,
  useDeleteInstallment,
  useCreateMpPreference,
  useCreateMpPix,
  type InstallmentWithRelations,
} from "@/hooks/usePayments";
import { useExpenses, useCreateExpense, useDeleteExpense } from "@/hooks/useExpenses";
import { Analytics } from "./Analytics";
import { format, isSameMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { effectiveInstallmentStatus } from "@/lib/installmentStatus";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatTile } from "@/components/shared/StatTile";
import { InstallmentStatusBadge } from "./InstallmentStatusBadge";
import { formatBRL } from "@/lib/format";

const fmt = (v: number) => formatBRL(v);

type FinanceTab = "receivables" | "expenses" | "permutas" | "analytics";

function resolveTab(param: string | null): FinanceTab {
  if (
    param === "receivables" ||
    param === "expenses" ||
    param === "permutas" ||
    param === "analytics"
  )
    return param;
  return "receivables";
}

const SEG_TRIGGER =
  "rounded-lg px-4 py-2 text-[12.5px] font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";

export function FinanceDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveTab(searchParams.get("tab"));

  const { data: stats, isLoading: statsLoading } = useFinanceStats();
  const { data: installments = [], isLoading: installmentsLoading } = useInstallments();
  const { data: expenses = [], isLoading: expensesLoading } = useExpenses();
  const { data: budgets = [] } = useBudgets();
  const { data: businessSettings } = useBusinessSettings();
  const markInstallment = useMarkInstallment();
  const deleteInstallment = useDeleteInstallment();
  const deleteExpense = useDeleteExpense();
  const mpPref = useCreateMpPreference();
  const mpPix = useCreateMpPix();
  const [pixModal, setPixModal] = useState<{ qr: string; b64: string; subtitle: string } | null>(
    null,
  );
  const [showAllExpenses, setShowAllExpenses] = useState(false);
  const [deleteInstallmentId, setDeleteInstallmentId] = useState<string | null>(null);
  const [deleteExpenseId, setDeleteExpenseId] = useState<string | null>(null);

  const now = new Date();
  const monthLabel = format(now, "MMMM", { locale: ptBR });
  const monthExpenses = useMemo(
    () => expenses.filter((e) => isSameMonth(new Date(e.incurred_on), now)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [expenses],
  );
  const visibleExpenses = showAllExpenses ? expenses : monthExpenses;
  const monthTotal = monthExpenses.reduce((s, e) => s + (e.amount || 0), 0);

  // Permuta agreed in the current period vs the cap from Configurações → Cobrança.
  const barter = useMemo(
    () => summarizeBarter(budgets, businessSettings),
    [budgets, businessSettings],
  );

  const planCounts = useMemo(() => {
    const m: Record<string, number> = {};
    installments.forEach((i) => {
      m[i.plan_id] = (m[i.plan_id] ?? 0) + 1;
    });
    return m;
  }, [installments]);

  const exportCSV = <T extends object>(rows: T[], name: string) => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]) as (keyof T)[];
    const csv = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTabChange = (value: string) => {
    const tab = resolveTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };

  const handleHeaderExport = () => {
    if (activeTab === "receivables") exportCSV(installments, "contas-a-receber");
    else if (activeTab === "expenses") exportCSV(expenses, "despesas");
    else if (activeTab === "permutas")
      exportCSV(
        barter.rows.map((r) => ({
          lead: r.leadName ?? "",
          proposta: r.title,
          percentual: r.percent,
          valor_permuta: r.barterTotal,
          a_cobrar: r.cashTotal,
          mensalidade_em_permuta: r.recurringInBarter,
          contrapartida: r.description ?? "",
          aceite: format(r.agreedAt, "yyyy-MM-dd"),
        })),
        `permutas-${barter.periodLabel.replace("/", "-")}`,
      );
  };

  const openPix = (i: InstallmentWithRelations) => {
    mpPix.mutate(
      { installment_id: i.id },
      {
        onSuccess: (r) =>
          setPixModal({
            qr: r.qr_code,
            b64: r.qr_code_base64,
            subtitle: `${i.clients?.name || "Cliente"} · parcela ${i.sequence}/${planCounts[i.plan_id] ?? 1} · ${fmt(i.amount)}`,
          }),
      },
    );
  };

  if (statsLoading || installmentsLoading || expensesLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const overdue = stats?.overdueCount ?? 0;

  return (
    <div className="mx-auto flex max-w-[1200px] flex-col gap-[22px]">
      <PageHeader
        title="Financeiro"
        description="Receita, contas a receber, despesas e lucro"
        actions={
          <Button
            variant="outline"
            className="h-[38px] rounded-[10px] px-4 text-[13px] font-semibold"
            onClick={handleHeaderExport}
            disabled={activeTab === "analytics"}
            title={
              activeTab === "analytics"
                ? "Exportação disponível nas abas Contas a receber e Despesas"
                : undefined
            }
          >
            Exportar CSV
          </Button>
        }
      />

      <div className="grid grid-cols-[repeat(auto-fit,minmax(215px,1fr))] gap-4">
        <StatTile
          label="Receita do mês"
          value={fmt(stats?.revenueMonth ?? 0)}
          foot="recebido até hoje"
          tone="ok"
        />
        <StatTile label="Receita no ano" value={fmt(stats?.revenueYTD ?? 0)} foot="acumulado" />
        <StatTile
          label="A receber"
          value={fmt(stats?.receivable ?? 0)}
          foot={overdue > 0 ? `${overdue} vencida${overdue === 1 ? "" : "s"}` : "tudo no prazo"}
          tone={overdue > 0 ? "bad" : "ok"}
        />
        <StatTile
          label="Lucro do mês"
          value={fmt(stats?.profitMonth ?? 0)}
          foot={`despesas ${fmt(stats?.expenseMonthly ?? 0)}`}
        />
        <StatTile label="MRR ativo" value={fmt(stats?.mrr ?? 0)} foot="mensalidades autorizadas" />
        <StatTile
          label={`Permutas no ${barter.period === "month" ? "mês" : "ano"}`}
          value={fmt(barter.agreed)}
          foot={
            barter.cap > 0
              ? barter.over
                ? `teto excedido em ${fmt(barter.agreed - barter.cap)}`
                : `de ${fmt(barter.cap)} · resta ${fmt(barter.remaining ?? 0)}`
              : `${barter.count} acordo${barter.count === 1 ? "" : "s"} · sem teto definido`
          }
          tone={barter.over ? "bad" : (barter.ratio ?? 0) >= 0.8 ? "warn" : "default"}
        />
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col gap-[22px]">
        <TabsList className="h-auto w-fit gap-[3px] rounded-[10px] bg-muted p-[3px]">
          <TabsTrigger value="receivables" className={SEG_TRIGGER}>
            Contas a receber
          </TabsTrigger>
          <TabsTrigger value="expenses" className={SEG_TRIGGER}>
            Despesas
          </TabsTrigger>
          <TabsTrigger value="permutas" className={SEG_TRIGGER}>
            Permutas
          </TabsTrigger>
          <TabsTrigger value="analytics" className={SEG_TRIGGER}>
            Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="receivables" className="mt-0">
          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="flex items-center justify-between border-b border-border px-6 pb-3.5 pt-5">
              <h2 className="text-[15px] font-bold">Contas a receber</h2>
              <span className="text-xs text-muted-foreground">
                {installments.length} parcela{installments.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                <div className="flex items-center gap-4 border-b border-border bg-muted/60 px-6 py-[11px] text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  <div className="min-w-0 flex-[1.4]">Cliente</div>
                  <div className="min-w-0 flex-[1.2]">Parcela</div>
                  <div className="w-[110px] shrink-0">Vencimento</div>
                  <div className="w-[120px] shrink-0 text-right">Valor</div>
                  <div className="w-[110px] shrink-0 text-right">Status</div>
                  <div className="w-[230px] shrink-0" />
                </div>
                {installments.length === 0 && (
                  <p className="py-8 text-center text-sm text-muted-foreground">
                    Sem parcelas cadastradas.
                  </p>
                )}
                {installments.map((i) => (
                  <div
                    key={i.id}
                    className="flex items-center gap-4 border-b border-border px-6 py-[15px] text-[13px] last:border-b-0 hover:bg-muted/40"
                  >
                    <div className="min-w-0 flex-[1.4] truncate font-semibold">
                      {i.clients?.name || "—"}
                    </div>
                    <div className="min-w-0 flex-[1.2] truncate text-muted-foreground">
                      {i.label || `Parcela ${i.sequence}`}
                      {(i.budgets?.client_projects?.title || i.budgets?.title) &&
                        ` · ${i.budgets?.client_projects?.title || i.budgets?.title}`}
                    </div>
                    <div className="w-[110px] shrink-0 text-muted-foreground">
                      {i.due_date ? format(new Date(i.due_date), "dd/MM/yyyy") : "—"}
                    </div>
                    <div className="w-[120px] shrink-0 text-right font-bold">{fmt(i.amount)}</div>
                    <div className="w-[110px] shrink-0 text-right">
                      <InstallmentStatusBadge status={effectiveInstallmentStatus(i)} />
                    </div>
                    <div className="flex w-[230px] shrink-0 justify-end gap-[7px]">
                      {i.public_token && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-[30px] w-[30px]"
                          title="Copiar link público"
                          aria-label="Copiar link público"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              `${window.location.origin}/pay/${i.public_token}`,
                            );
                            toast.success("Link copiado");
                          }}
                        >
                          <LinkIcon className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {i.status !== "paid" && (
                        <>
                          <Button
                            size="icon"
                            variant="outline"
                            className="h-[30px] w-[30px] rounded-lg"
                            title="Gerar link de cartão"
                            aria-label="Gerar link de cartão"
                            onClick={() =>
                              mpPref.mutate({ installment_id: i.id, max_installments: 12 })
                            }
                            disabled={mpPref.isPending}
                          >
                            <CreditCard className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            className="h-[30px] rounded-lg px-[11px] text-[11.5px] font-semibold"
                            onClick={() => openPix(i)}
                            disabled={mpPix.isPending}
                          >
                            PIX
                          </Button>
                          <Button
                            variant="outline"
                            className="h-[30px] rounded-lg px-[11px] text-[11.5px] font-semibold"
                            onClick={() => markInstallment.mutate({ id: i.id, status: "paid" })}
                          >
                            Marcar pago
                          </Button>
                        </>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-[30px] w-[30px] text-bad hover:text-bad"
                        title="Excluir parcela"
                        aria-label="Excluir parcela"
                        onClick={() => setDeleteInstallmentId(i.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="expenses" className="mt-0">
          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-6 pb-3.5 pt-5">
              <h2 className="text-[15px] font-bold capitalize">
                {showAllExpenses ? "Todas as despesas" : `Despesas de ${monthLabel}`}
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-[12.5px] text-muted-foreground">
                  Total{" "}
                  <strong className="text-foreground">
                    {fmt(
                      showAllExpenses
                        ? expenses.reduce((s, e) => s + (e.amount || 0), 0)
                        : monthTotal,
                    )}
                  </strong>
                </span>
                <button
                  type="button"
                  onClick={() => setShowAllExpenses((v) => !v)}
                  className="text-[12px] font-semibold text-primary hover:underline"
                >
                  {showAllExpenses ? "Só este mês" : "Ver todas"}
                </button>
                <ExpenseDialog />
              </div>
            </div>
            {visibleExpenses.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Sem despesas cadastradas.
              </p>
            )}
            {visibleExpenses.map((e) => (
              <div
                key={e.id}
                className="flex items-center gap-4 border-b border-border px-6 py-[15px] text-[13px] last:border-b-0 hover:bg-muted/40"
              >
                <div className="min-w-0 flex-[1.4] truncate font-semibold">{e.description}</div>
                <div className="min-w-0 flex-1">
                  <span className="inline-flex h-[23px] items-center rounded-[7px] bg-muted px-2.5 text-[11px] font-bold text-muted-foreground">
                    {e.category}
                  </span>
                </div>
                <div className="w-[120px] text-muted-foreground">
                  {format(new Date(e.incurred_on), "dd/MM/yyyy", { locale: ptBR })}
                </div>
                <div className="w-[120px] text-right font-bold">{fmt(e.amount)}</div>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-[30px] w-[30px] shrink-0"
                  aria-label="Excluir despesa"
                  onClick={() => setDeleteExpenseId(e.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </section>
        </TabsContent>

        <TabsContent value="permutas" className="mt-0 flex flex-col gap-[22px]">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.4fr_1fr]">
            <section className="flex flex-col gap-4 rounded-2xl border border-border bg-card px-6 py-5 shadow-card">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="flex items-center gap-2 text-[15px] font-bold">
                    <Handshake className="h-4 w-4 text-primary" /> Permutas acordadas —{" "}
                    {barter.periodLabel}
                  </h2>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                    Propostas assinadas/aceitas no período com parte do valor liquidada em serviços.
                  </p>
                </div>
                <Link
                  to="/admin/settings?tab=billing"
                  className="text-[12.5px] font-semibold text-primary hover:underline"
                >
                  {barter.cap > 0 ? "Ajustar teto" : "Definir teto"}
                </Link>
              </div>
              {barter.cap > 0 ? (
                <div className="flex flex-col gap-2">
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-medium text-muted-foreground">Usado</div>
                      <div className="font-display text-2xl font-bold tracking-[-0.035em]">
                        {fmt(barter.agreed)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[11px] font-medium text-muted-foreground">
                        {barter.over ? "Excedido" : "Disponível"}
                      </div>
                      <div
                        className={`font-display text-2xl font-bold tracking-[-0.035em] ${barter.over ? "text-bad" : "text-ok"}`}
                      >
                        {fmt(barter.over ? barter.agreed - barter.cap : (barter.remaining ?? 0))}
                      </div>
                    </div>
                  </div>
                  <Progress
                    value={Math.min(100, Math.round((barter.ratio ?? 0) * 100))}
                    className="h-2.5"
                    indicatorClassName={
                      barter.over ? "bg-bad" : (barter.ratio ?? 0) >= 0.8 ? "bg-warn" : "bg-primary"
                    }
                    aria-label="Uso do teto de permuta"
                  />
                  <div className="text-[11.5px] text-muted-foreground">
                    {Math.round((barter.ratio ?? 0) * 100)}% do teto de {fmt(barter.cap)} por{" "}
                    {barter.period === "month" ? "mês" : "ano"}
                  </div>
                </div>
              ) : (
                <p className="text-[13px] text-muted-foreground">
                  Sem teto definido — {fmt(barter.agreed)} acordado em {barter.count} permuta
                  {barter.count === 1 ? "" : "s"} no período.
                </p>
              )}
            </section>

            <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card px-6 py-5 shadow-card">
              <h2 className="text-[15px] font-bold">Dinheiro × permuta</h2>
              <p className="-mt-2 text-[12.5px] text-muted-foreground">
                Negócios fechados em {barter.periodLabel}.
              </p>
              {(() => {
                const total = barter.cashAgreed + barter.agreed;
                const cashPct = total > 0 ? Math.round((barter.cashAgreed / total) * 100) : 0;
                const barterPct = total > 0 ? 100 - cashPct : 0;
                return (
                  <>
                    <div
                      className="flex h-2.5 w-full overflow-hidden rounded-full bg-secondary"
                      role="img"
                      aria-label={`${cashPct}% em dinheiro, ${barterPct}% em permuta`}
                    >
                      <div className="h-full bg-ok" style={{ width: `${cashPct}%` }} />
                      <div className="h-full bg-primary" style={{ width: `${barterPct}%` }} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                          <span className="h-2 w-2 rounded-full bg-ok" /> Em dinheiro
                        </div>
                        <div className="font-display text-lg font-bold tracking-[-0.03em]">
                          {fmt(barter.cashAgreed)}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground">{cashPct}%</div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
                          <span className="h-2 w-2 rounded-full bg-primary" /> Em permuta
                        </div>
                        <div className="font-display text-lg font-bold tracking-[-0.03em]">
                          {fmt(barter.agreed)}
                        </div>
                        <div className="text-[11.5px] text-muted-foreground">{barterPct}%</div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </section>
          </div>

          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="flex items-center justify-between border-b border-border px-6 pb-3.5 pt-5">
              <h2 className="text-[15px] font-bold">Acordos de permuta</h2>
              <span className="text-xs text-muted-foreground">
                {barter.count} acordo{barter.count === 1 ? "" : "s"}
              </span>
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[860px]">
                <div className="flex items-center gap-4 border-b border-border bg-muted/60 px-6 py-[11px] text-[11px] font-bold uppercase tracking-[0.05em] text-muted-foreground">
                  <div className="min-w-0 flex-[1.2]">Cliente</div>
                  <div className="min-w-0 flex-[1.4]">Proposta</div>
                  <div className="w-[70px] shrink-0 text-right">%</div>
                  <div className="w-[120px] shrink-0 text-right">Em permuta</div>
                  <div className="w-[110px] shrink-0 text-right">A cobrar</div>
                  <div className="min-w-0 flex-[1.6]">Contrapartida</div>
                  <div className="w-[90px] shrink-0 text-right">Aceite</div>
                </div>
                {barter.rows.length === 0 && (
                  <div className="px-6 py-8 text-center text-[13px] text-muted-foreground">
                    Nenhuma permuta acordada em {barter.periodLabel}.
                  </div>
                )}
                {barter.rows.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-4 border-b border-border px-6 py-3 text-[13px] last:border-b-0"
                  >
                    <div className="min-w-0 flex-[1.2] truncate font-semibold">
                      {r.leadId ? (
                        <Link
                          to={`/admin/leads/${r.leadId}?tab=pagamentos`}
                          className="hover:underline"
                        >
                          {r.leadName ?? "Lead"}
                        </Link>
                      ) : (
                        (r.leadName ?? "—")
                      )}
                    </div>
                    <div
                      className="min-w-0 flex-[1.4] truncate text-muted-foreground"
                      title={r.title}
                    >
                      {r.title}
                    </div>
                    <div className="w-[70px] shrink-0 text-right">{r.percent}%</div>
                    <div className="w-[120px] shrink-0 text-right font-bold">
                      {fmt(r.barterTotal)}
                      {r.recurringInBarter > 0 && (
                        <div className="text-[11px] font-normal text-muted-foreground">
                          + {fmt(r.recurringInBarter)}/mês em permuta
                        </div>
                      )}
                    </div>
                    <div className="w-[110px] shrink-0 text-right text-muted-foreground">
                      {fmt(r.cashTotal)}
                    </div>
                    <div
                      className="min-w-0 flex-[1.6] truncate text-muted-foreground"
                      title={r.description ?? undefined}
                    >
                      {r.description ?? "—"}
                    </div>
                    <div className="w-[90px] shrink-0 text-right text-muted-foreground">
                      {format(r.agreedAt, "dd/MM/yy")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="analytics" className="mt-0">
          <Analytics embedded timeline={stats?.timeline} />
        </TabsContent>
      </Tabs>

      <Dialog open={!!pixModal} onOpenChange={(o) => !o && setPixModal(null)}>
        <DialogContent className="w-[min(400px,92vw)] overflow-hidden rounded-[18px] p-0 text-center">
          <DialogHeader className="space-y-1 px-6 pb-2 pt-6 text-center sm:text-center">
            <DialogTitle className="text-[17px] font-bold tracking-[-0.02em]">
              PIX gerado
            </DialogTitle>
            <DialogDescription className="text-[12.5px] text-muted-foreground">
              {pixModal?.subtitle}
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-5">
            <div className="mx-auto flex h-[180px] w-[180px] items-center justify-center overflow-hidden rounded-[14px] border border-border bg-muted">
              {pixModal?.b64 && (
                <img
                  src={`data:image/png;base64,${pixModal.b64}`}
                  alt="QR Code PIX"
                  className="h-full w-full object-contain"
                />
              )}
            </div>
            <div className="mt-4 break-all rounded-[10px] border border-border bg-muted px-[13px] py-[11px] text-left font-mono text-[11px] text-muted-foreground">
              {pixModal?.qr}
            </div>
          </div>
          <div className="flex gap-[9px] px-6 pb-[22px]">
            <Button
              variant="outline"
              className="h-10 flex-1 rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => setPixModal(null)}
            >
              Fechar
            </Button>
            <Button
              className="h-10 flex-1 rounded-[10px] text-[12.5px] font-semibold"
              onClick={() => {
                if (pixModal?.qr) {
                  navigator.clipboard.writeText(pixModal.qr);
                  toast.success("Código PIX copiado");
                }
              }}
            >
              Copiar código
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteInstallmentId}
        onOpenChange={(o) => !o && setDeleteInstallmentId(null)}
      >
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Excluir parcela?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              A parcela será removida definitivamente, junto com o registro de pagamento associado,
              se houver. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
              onClick={() => {
                if (deleteInstallmentId) deleteInstallment.mutate(deleteInstallmentId);
                setDeleteInstallmentId(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteExpenseId} onOpenChange={(o) => !o && setDeleteExpenseId(null)}>
        <AlertDialogContent className="max-w-[430px] rounded-[18px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[17px] tracking-[-0.02em]">
              Excluir despesa?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] leading-[1.6]">
              A despesa será removida definitivamente. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-[38px] rounded-[10px] text-[12.5px] font-semibold">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              className="h-[38px] rounded-[10px] bg-bad text-[12.5px] font-semibold text-bad-foreground hover:bg-bad/90"
              onClick={() => {
                if (deleteExpenseId) deleteExpense.mutate(deleteExpenseId);
                setDeleteExpenseId(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExpenseDialog() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({
    category: "ferramentas",
    description: "",
    amount: 0,
    recurring: "none" as "none" | "monthly" | "yearly",
    incurred_on: new Date().toISOString().slice(0, 10),
    client_project_id: null as string | null,
    notes: "",
  });
  const create = useCreateExpense();

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-8 rounded-lg px-[13px] text-xs font-semibold">Nova despesa</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nova despesa</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="expense-categoria">Categoria</Label>
              <Select
                value={data.category}
                onValueChange={(v) => setData({ ...data, category: v })}
              >
                <SelectTrigger id="expense-categoria">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ferramentas">Ferramentas</SelectItem>
                  <SelectItem value="impostos">Impostos</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="infraestrutura">Infraestrutura</SelectItem>
                  <SelectItem value="outros">Outros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="expense-recorrencia">Recorrência</Label>
              <Select
                value={data.recurring}
                onValueChange={(v: "none" | "monthly" | "yearly") =>
                  setData({ ...data, recurring: v })
                }
              >
                <SelectTrigger id="expense-recorrencia">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Única</SelectItem>
                  <SelectItem value="monthly">Mensal</SelectItem>
                  <SelectItem value="yearly">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="expense-descricao">Descrição</Label>
            <Input
              id="expense-descricao"
              value={data.description}
              onChange={(e) => setData({ ...data, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="expense-valor-r">Valor (R$)</Label>
              <Input
                id="expense-valor-r"
                type="number"
                step="0.01"
                value={data.amount}
                onChange={(e) => setData({ ...data, amount: +e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="expense-data">Data</Label>
              <Input
                id="expense-data"
                type="date"
                value={data.incurred_on}
                onChange={(e) => setData({ ...data, incurred_on: e.target.value })}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="expense-notas">Notas</Label>
            <Textarea
              id="expense-notas"
              value={data.notes}
              onChange={(e) => setData({ ...data, notes: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              create.mutate(data, { onSuccess: () => setOpen(false) });
            }}
          >
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
